import React from 'react';
import { SharingDialog } from './';
import { Button, Dialog, IconButton, Tooltip } from '@material-ui/core';
import { Close, Info, VpnKey } from '@material-ui/icons';
import UserSelector from '../AutocompleteSelector/UserSelector';
import { func, oneOf, string } from 'prop-types';
import {
  DIALOG_ASSIGN_OWNER,
  DIALOG_SHARE_ADVERTISER,
  DIALOG_SHARE_CAMPAIGN,
  EDITOR,
  OWNER,
  VIEWER
} from '../../helpers/constant';

import classnames from 'classnames';
import styles from './styles.scss';

export class AssignOwnerDialog extends SharingDialog {
  static propTypes = {
    dialogName: oneOf([DIALOG_SHARE_ADVERTISER, DIALOG_SHARE_CAMPAIGN, DIALOG_ASSIGN_OWNER]),
    cancelTitle: string,
    redirectToHomePage: func,
    setAssignLabel: string,
    openDialog: func,
    currentUserId: string
  };

  static defaultProps = {
    label: 'Assign Owner',
    setAssignLabel: 'Owner',
    btnAddLabel: 'ASSIGN',
    btnCancelLabel: 'CANCEL',
    approvedListTitle: 'Assign an owner to ${entityName}',
    autoCompleteSubtitle: 'Grants access to this Advertiser and all underlying Campaigns',
    description: `This advertiser is without an owner. It's likely the prior owner was deactivated before the advertiser was shared.`,
    cancelTitle: `An owner must be assigned in order to use this advertiser and its campaigns. Cancelling this action will route to the homepage.`,
  };

  state = {
    enableAddBtn: true,
    isAssignButtonDisabled: true,
    newSelectedIds: []
  };

  //---------------------------------------------------------------------
  //------------------------Interaction Functions------------------------
  //---------------------------------------------------------------------

  handleSelect = (newSelectedIds) => {
    this.setState({
      isAssignButtonDisabled: newSelectedIds.length !== 1,
      newSelectedIds
    });
  }

  handleAddBtnClick = () => {
    const { id, openDialog, currentUserId } = this.props;
    const { editors, newSelectedIds } = this.state;
    const selectedOwnerId = get(newSelectedIds, ['0'])

    this.handleSubmit();

    // Open the sharing dialog only if user is an owner or an editor. Otherwise, redirect the user to the homepage.
    if (selectedOwnerId === currentUserId || editors.includes(currentUserId)) {
      openDialog && openDialog(DIALOG_SHARE_ADVERTISER, id);
    } else {
      this.handleRedirect();
    }
  };

  handleSubmit = () => {
    const { id, onChange, usersHashMap } = this.props;
    const { editors, viewers, newSelectedIds } = this.state;
    const selectedUsers = [];

    const selectedId = newSelectedIds.length && newSelectedIds[0];
    const owner = cloneDeep(usersHashMap[selectedId]);
    if (owner) {
      owner.role = OWNER;
      selectedUsers.push(owner);
    }

    // Remove the owner from the editor/viewer data pool in case it had previous permission.
    const editorsPool = editors.reduce((accum, editorId) => {
      if (editorId !== selectedId && usersHashMap[editorId]) {
        const editor = cloneDeep(usersHashMap[editorId]);
        editor.role = EDITOR;
        accum.push(editor);
      }
      return accum;
    }, []);

    const viewersPool = viewers.reduce((accum, viewerId) => {
      if (viewerId !== selectedId && usersHashMap[viewerId]) {
        const viewer = cloneDeep(usersHashMap[viewerId]);
        viewer.role = VIEWER;
        accum.push(viewer);
      }
      return accum;
    }, []);
    selectedUsers.push(...editorsPool);
    selectedUsers.push(...viewersPool);
    onChange && onChange(id, selectedUsers);
    this.handleClose();
  };

  handleClose = () => {
    const { onClose, dialogName } = this.props;
    this.setState({
      newSelectedIds: [],
      isAssignButtonDisabled: true
    });
    onClose && onClose(dialogName);
  };

  handleRedirect = () => {
    const { redirectToHomePage } = this.props;
    redirectToHomePage && redirectToHomePage();
  };

  handleCancel = () => {
    this.handleClose();
    this.handleRedirect();
  };

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------

  drawUserSelector() {
    const {
      usersHashMap,
      autoCompleteSubtitle,
      approvedListTitle,
      entityName,
      setAssignLabel,
    } = this.props;
    const listTitle = entityName && approvedListTitle.replace('${entityName}', entityName);
    const { newSelectedIds } = this.state;
    const dataPool = cloneDeep(usersHashMap);

    return (
      <div className={styles.selector}>
        <div className={styles.approvedListTitle}>{listTitle}</div>
        <div className={styles.selectorSubtitle}>{autoCompleteSubtitle}</div>
        <div className={styles.selectorSearch}>
          <div className={styles.searchBar}>
            <UserSelector
              dataPool={Object.values(dataPool)}
              selectedIds={newSelectedIds}
              onChange={this.handleSelect}
            />
          </div>
          <span className={styles.ownerIcon}><VpnKey/>{setAssignLabel}</span>
          {this.drawSelectorPopover()}
        </div>
      </div>
    );
  }

  drawAddActions() {
    const { btnAddLabel, btnCancelLabel } = this.props;
    const { isAssignButtonDisabled } = this.state;

    return (
      <div className={styles.addAccess}>
        <Button
          className={styles.btnAdd}
          onClick={this.handleAddBtnClick}
          color='primary'
          variant='contained'
          disabled={isAssignButtonDisabled}
        >
          {btnAddLabel}
        </Button>
        <Button onClick={this.handleCancel}>{btnCancelLabel}</Button>
      </div>
    )
  }

  render() {
    const { open, label, description, className, cancelTitle } = this.props;

    return (
      <Dialog open={open} className={classnames(styles.sharingDialog, styles.assignOwnerDialog, className)} scroll='body'>
        <div className={styles.dialogTitle}>
          <div className={styles.title}>
            <span>{label}</span>
            <Tooltip title={description}>
              <span className={styles.icon}><Info/></span>
            </Tooltip>
          </div>
          <IconButton onClick={this.handleCancel}>
            <Close/>
          </IconButton>
        </div>
        {this.drawUserSelector()}
        <div className={styles.cancelContainer}>
          <div className={styles.cancelTitle}>{cancelTitle}</div>
        </div>
        {this.drawAddActions()}
      </Dialog>
    );
  }
}

import { connect } from 'react-redux';
import { get, cloneDeep } from 'lodash';
import { redirectToHomePage } from '../../state/modules/routing/actions';
import {
  loadAdvertisers,
  namespace as advertiserNameSpace,
  updateAdvertiserPermission
} from '../../state/modules/app/advertiser';
import {
  namespace as userManagmentNamespace,
  loadOrgUsers,
  loadTeams
} from '../../state/modules/app/userManagement/actions';
import {
  namespace as dialogNameSpace,
  Status as dialogStatus,
  closeDialog,
  openDialog
} from '../../state/modules/app/dialogStatus/actions';

const AssignOwnerDialogSmartComponent = connect(
  state => {
    const openStatus = get(state, [dialogNameSpace, 'dialogs', DIALOG_ASSIGN_OWNER, 'status']);
    const id = get(state, [dialogNameSpace, 'dialogs', DIALOG_ASSIGN_OWNER, 'value']);
    const currentAdvertiser = get(state, [advertiserNameSpace, 'byId', id], {});
    const entityName = get(currentAdvertiser, 'advertiser_name', 'this advertiser');
    const editors = get(currentAdvertiser, 'editors', []);
    const viewers = get(currentAdvertiser, 'viewers', []);
    const users = get(state, [userManagmentNamespace, 'users', 'data'], []);
    const userLoadStatus = get(state, [userManagmentNamespace, 'users', 'status']);
    const teamLoadStatus = get(state, [userManagmentNamespace, 'teams', 'status']);
    const currentUserId = get(state, 'user.user.userId');

    const usersHashMap = users.reduce((accum, user) => {
      if (user.id) {
        accum[user.id] = {...user};
      }
      return accum;
    }, {});

    return {
      id,
      dialogName: DIALOG_ASSIGN_OWNER,
      open: openStatus === dialogStatus.STATUS_OPEN,
      entityName,
      users,
      editors,
      viewers,
      usersHashMap,
      currentUserId,
      userLoadStatus,
      teamLoadStatus,
    };
  },
  {
    loadOrgUsers,
    loadTeams,
    loadAdvertisers,
    onChange: updateAdvertiserPermission,
    onClose: closeDialog,
    openDialog,
    redirectToHomePage
  }
)(AssignOwnerDialog);

export default AssignOwnerDialogSmartComponent;
